<template>
  <div class="w-full h-full">
    <!-- Order detail. A drawer rather than a route so the list keeps its page and filters
         when an operator closes it. -->
    <Drawer
      direction="right"
      :open="showDetail"
      @update:open="(o) => { if (!o) closeDetail() }"
    >
      <DrawerContent class="sm:max-w-md">
        <DrawerHeader class="text-left">
          <DrawerTitle>{{ selected?.brandName || 'Gift card order' }}</DrawerTitle>
          <DrawerDescription>
            Placed {{ selected ? formatDateTime(selected.createdOn) : '' }}
          </DrawerDescription>
        </DrawerHeader>

        <div v-if="selected" class="flex flex-col gap-4 overflow-y-auto px-4 flex-1">
          <div class="flex items-center gap-3">
            <img
              v-if="selected.brandImageUrl"
              class="w-10 h-10 rounded-md border border-border object-contain shrink-0 bg-white"
              :src="selected.brandImageUrl"
              alt=""
            />
            <div class="min-w-0">
              <p class="text-sm font-medium truncate">{{ selected.brandName }}</p>
              <p class="text-xs text-muted-foreground">{{ selected.brandCode }}</p>
            </div>
            <Badge :variant="statusVariant(selected.status)" class="ml-auto whitespace-nowrap">
              {{ selected.status }}
            </Badge>
          </div>

          <dl class="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            <div>
              <dt class="text-xs text-muted-foreground">Value</dt>
              <dd class="tabular-nums font-medium">
                {{ formatCents(selected.priceInCents, selected.currency) }}
              </dd>
            </div>
            <div>
              <dt class="text-xs text-muted-foreground">Currency</dt>
              <dd>{{ selected.currency || '—' }}</dd>
            </div>
            <div>
              <dt class="text-xs text-muted-foreground">End user</dt>
              <dd class="truncate" :title="selected.endUserId">{{ selected.endUserId || '—' }}</dd>
            </div>
            <div>
              <dt class="text-xs text-muted-foreground">Attempts</dt>
              <dd class="tabular-nums">{{ selected.attempts ?? 0 }}</dd>
            </div>
            <div>
              <dt class="text-xs text-muted-foreground">Your commission</dt>
              <dd class="tabular-nums font-medium">
                {{ formatCents(selected.publisherCommissionInCents, selected.currency) }}
              </dd>
            </div>
            <div class="col-span-2">
              <dt class="text-xs text-muted-foreground">Order ID</dt>
              <dd><CopyId :value="selected.id" :max-width="280" /></dd>
            </div>
            <div class="col-span-2">
              <dt class="text-xs text-muted-foreground">Your reference</dt>
              <dd>
                <CopyId v-if="selected.clientOrderId" :value="selected.clientOrderId" :max-width="280" />
                <span v-else class="text-muted-foreground">—</span>
              </dd>
            </div>
            <div class="col-span-2">
              <dt class="text-xs text-muted-foreground">Fulfilled</dt>
              <dd>{{ formatDateTime(selected.fulfilledOn) }}</dd>
            </div>
            <div class="col-span-2">
              <dt class="text-xs text-muted-foreground">Claim by</dt>
              <dd>
                <span v-if="selected.expiresOn">{{ formatDateTime(selected.expiresOn) }}</span>
                <span v-else class="text-muted-foreground">No deadline</span>
              </dd>
            </div>
          </dl>

          <div v-if="selected.failureMessage" class="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2">
            <p class="text-xs font-medium text-destructive">Failure reason</p>
            <p class="mt-1 text-sm text-destructive">{{ selected.failureMessage }}</p>
          </div>
        </div>

        <DrawerFooter class="flex-row justify-end gap-2">
          <Button variant="outline" @click="closeDetail()">Close</Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>

    <Card>
      <CardHeader>
        <div>
          <CardTitle>Gift card orders</CardTitle>
          <CardDescription>
            Every gift card purchased for your end users, newest first.
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent>
        <DataTable
          :headers="headers"
          :items="orders"
          :loading="loading"
          :error="error"
          :server-options="serverOptions"
          :server-items-length="total"
          searchable
          search-placeholder="Search by order ID, reference, brand or end user"
          :search="searchInput"
          :filter-groups="filterGroups"
          :filters="filters"
          @update:server-options="onServerOptions"
          @update:search="searchInput = $event"
          @search="onSearch"
          @clear-search="onClearSearch"
          @filter-change="onFilterChange"
          @retry="fetchOrders(serverOptions.page - 1, serverOptions.rowsPerPage)"
        >
          <template #empty-message>
            <div class="flex justify-center py-4 text-muted-foreground text-sm">
              No gift card orders yet
            </div>
          </template>

          <template #item-order="order">
            <div class="flex items-center gap-2.5">
              <img
                v-if="order.brandImageUrl"
                class="w-8 h-8 rounded-md border border-border object-contain shrink-0 bg-white"
                :src="order.brandImageUrl"
                alt=""
              />
              <div class="min-w-0">
                <p class="text-sm font-medium truncate" :title="order.brandName">
                  {{ order.brandName }}
                </p>
                <p class="text-xs text-muted-foreground mt-1 truncate">{{ order.brandCode }}</p>
              </div>
            </div>
          </template>

          <template #item-id="{ id }">
            <CopyId :value="id" :max-width="120" />
          </template>

          <template #item-endUserId="{ endUserId }">
            <span v-if="endUserId" class="text-sm truncate" :title="endUserId">{{ endUserId }}</span>
            <span v-else class="text-muted-foreground">—</span>
          </template>

          <template #item-value="order">
            <span class="text-sm tabular-nums whitespace-nowrap">
              {{ formatCents(order.priceInCents, order.currency) }}
            </span>
          </template>

          <template #item-status="{ status }">
            <Badge :variant="statusVariant(status)" class="whitespace-nowrap">{{ status }}</Badge>
          </template>

          <template #item-createdOn="{ createdOn }">
            <FormatDate :value="createdOn" format="datetime-short" />
          </template>

          <template #item-action="order">
            <div class="flex justify-end">
              <Button variant="outline" size="sm" @click="openDetail(order)">Details</Button>
            </div>
          </template>
        </DataTable>
      </CardContent>
    </Card>
  </div>
</template>

<script setup></script>

<script>
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Badge,
  CopyId,
  FormatDate,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from '@boogi/ui'
import { DataTable } from '@boogi/ui'

export default {
  name: 'gift-card-orders',
  components: {
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    Button,
    Badge,
    CopyId,
    FormatDate,
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerTitle,
    DrawerDescription,
    DrawerFooter,
    DataTable,
  },
  props: {},

  data() {
    return {
      loading: true,
      error: false,

      serverOptions: { page: 1, rowsPerPage: 25 },
      total: undefined,

      searchInput: '',
      appliedSearch: '',
      selectedStatuses: [],

      orders: [],
      selected: undefined,
      showDetail: false,

      headers: [
        { text: 'Gift card', value: 'order', width: 260 },
        { text: 'Order ID', value: 'id' },
        { text: 'End user', value: 'endUserId' },
        { text: 'Value', value: 'value' },
        { text: 'Status', value: 'status' },
        { text: 'Placed', value: 'createdOn' },
        { text: 'Action', value: 'action' },
      ],
      statuses: ['PENDING', 'AWAITING_FUNDS', 'PROCESSING', 'FULFILLED', 'FAILED', 'CANCELED'],
    }
  },

  created() {
    this.fetchOrders(0, this.serverOptions.rowsPerPage)
  },

  computed: {
    filterGroups() {
      return [
        {
          key: 'status',
          label: 'Status',
          options: this.statuses.map((s) => ({ value: s, label: s })),
        },
      ]
    },
    filters() {
      return { status: this.selectedStatuses }
    },
  },

  methods: {
    apiUrl() {
      return localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
    },

    authHeaders() {
      const token = localStorage.getItem('token')
      return { 'Content-Type': 'application/json', Authorization: `${token}` }
    },

    onServerOptions(next) {
      this.serverOptions = next
      this.fetchOrders(next.page - 1, next.rowsPerPage)
    },

    onSearch(value) {
      this.appliedSearch = value
      this.serverOptions = { ...this.serverOptions, page: 1 }
      this.fetchOrders(0, this.serverOptions.rowsPerPage)
    },

    onClearSearch() {
      this.searchInput = ''
      this.appliedSearch = ''
      this.serverOptions = { ...this.serverOptions, page: 1 }
      this.fetchOrders(0, this.serverOptions.rowsPerPage)
    },

    onFilterChange(updated) {
      this.selectedStatuses = (updated && updated.status) || []
      this.serverOptions = { ...this.serverOptions, page: 1 }
      this.fetchOrders(0, this.serverOptions.rowsPerPage)
    },

    async fetchOrders(page, size) {
      this.loading = true
      this.error = false
      try {
        const params = new URLSearchParams()
        params.append('page', page)
        params.append('size', size)
        if (this.appliedSearch) params.append('search', this.appliedSearch)
        if (this.selectedStatuses.length) {
          params.append('status', this.selectedStatuses.join(','))
        }

        const res = await this.$apiFetch(
          `${this.apiUrl()}/private/api/client/gift-card/orders?${params.toString()}`,
          { headers: this.authHeaders() }
        )

        if (res && res.ok) {
          const pageData = await res.json()
          this.orders = pageData.content || []
          this.total = pageData.totalElements
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },

    openDetail(order) {
      this.selected = order
      this.showDetail = true
    },

    closeDetail() {
      this.showDetail = false
      this.selected = undefined
    },

    statusVariant(status) {
      const s = String(status || '').toUpperCase()
      if (s === 'FULFILLED') return 'success'
      if (s === 'FAILED' || s === 'CANCELED') return 'destructive'
      if (s === 'PENDING' || s === 'PROCESSING' || s === 'AWAITING_FUNDS') return 'warning'
      return 'secondary'
    },

    /**
     * Renders a minor-unit amount as currency.
     *
     * <p>Amounts arrive in minor units, so they are divided by 100 rather than assumed to be major
     * units already.
     */
    formatCents(cents, currency) {
      if (cents == null) return '—'
      const amount = Number(cents) / 100
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currency || 'GBP',
        }).format(amount)
      } catch {
        return `${amount.toFixed(2)} ${currency || ''}`.trim()
      }
    },

    formatDateTime(value) {
      if (!value) return '—'
      try {
        return new Intl.DateTimeFormat(undefined, {
          dateStyle: 'medium',
          timeStyle: 'short',        }).format(new Date(value))
      } catch {
        return '—'
      }
    },
  },
}
</script>
